<template>
    <div class="question-list-detail animation-fadeIn">
        <!-- Premium Page Header -->
        <div class="page-header-container">
            <div class="page-header">
                <div class="header-nav">
                    <el-button link class="back-btn" @click="$router.push('/question-lists')">
                        <el-icon>
                            <ArrowLeft />
                        </el-icon> 返回广场
                    </el-button>
                </div>

                <div v-if="list" class="header-content">
                    <div class="title-row">
                        <h1 class="list-title">{{ list.title }}</h1>
                        <el-tag :type="list.isPublic ? 'success' : 'info'" effect="dark" class="status-badge">
                            {{ list.isPublic ? 'PUBLIC' : 'PRIVATE' }}
                        </el-tag>
                        <el-button :type="list.isFavorite ? 'warning' : 'info'" link class="favorite-action-btn"
                            @click="toggleFavorite">
                            <el-icon :size="24">
                                <component :is="list.isFavorite ? 'StarFilled' : 'Star'" />
                            </el-icon>
                            <span class="btn-text">{{ list.isFavorite ? '已收藏' : '收藏' }}</span>
                        </el-button>
                    </div>
                    <p class="list-description">{{ list.description || '这一卷繁星，皆是为你精挑细选的智慧之光。' }}</p>

                    <div class="list-meta">
                        <span class="meta-item">
                            <el-icon>
                                <Calendar />
                            </el-icon> {{ formatDate(list.createdAt) }}
                        </span>
                        <span class="meta-item">
                            <el-icon>
                                <Document />
                            </el-icon> {{ list.questions?.length || 0 }} 道精选试题
                        </span>
                        <span class="meta-item" v-if="isOwner">
                            <el-icon>
                                <User />
                            </el-icon> 我的私藏
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="loading && !list" v-loading="loading" class="loading-state"></div>

        <template v-if="list">
            <div class="content-body">
                <el-row :gutter="24">
                    <!-- Left Column: Tabs Content -->
                    <el-col :span="18">
                        <el-card shadow="never" class="main-content-card">
                            <el-tabs v-model="activeTab" class="custom-tabs">
                                <!-- Introduction Tab -->
                                <el-tab-pane label="题单简介" name="intro">
                                    <div class="intro-section">
                                        <div class="section-title">题单简介</div>
                                        <MdEditor preview-only preview-theme="cyanosis"
                                            :model-value="list.description || '还没有编写题单简介呢。'" />
                                    </div>
                                </el-tab-pane>

                                <!-- Questions Tab -->
                                <el-tab-pane label="题目列表" name="questions">
                                    <!-- Actions Bar inside Questions Tab -->
                                    <div class="actions-embedded-bar">
                                        <div class="bar-info">已包含 {{ list.questions?.length || 0 }} 道试题</div>
                                        <div class="bar-actions">
                                            <el-button type="success" size="small"
                                                @click="addAllToBasket">加入试题篮</el-button>
                                            <el-button type="warning" size="small" @click="handleExport">导出
                                                Word</el-button>
                                        </div>
                                    </div>

                                    <div class="questions-container">
                                        <el-empty v-if="!list.questions || list.questions.length === 0"
                                            description="题单中还没有题目，快去题库添加吧" />

                                        <div v-for="(q, index) in list.questions" :key="q.id"
                                            class="q-item-wrapper paper-style-entry">
                                            <div class="q-content-wrapper">
                                                <div class="q-main-body">
                                                    <div class="q-badge-group">
                                                        <el-tag size="small" type="primary" effect="plain">{{
                                                            getTypeName(q.type) }}</el-tag>
                                                        <el-tag size="small" type="info" plain>{{
                                                            getDifficultName(q.difficult) }}</el-tag>

                                                        <el-tag v-if="q.source" size="small" type="warning"
                                                            effect="light" round>
                                                            <el-icon>
                                                                <Platform />
                                                            </el-icon> {{ q.source }}
                                                        </el-tag>
                                                        <el-tag v-if="q.region || q.city" size="small" type="success"
                                                            effect="light" round>
                                                            <el-icon>
                                                                <Location />
                                                            </el-icon> {{ q.region }}{{ q.city ? ' · ' + q.city : '' }}
                                                        </el-tag>
                                                    </div>
                                                    <div class="q-content-preview">
                                                        <MdEditor preview-only preview-theme="cyanosis"
                                                            :model-value="(index + 1) + '. ' + (q.content || '')" />
                                                    </div>
                                                </div>
                                                <div class="q-hover-actions">
                                                    <el-button link type="success" size="small"
                                                        @click="handleAddToBasket(q)">
                                                        <el-icon>
                                                            <DocumentCopy />
                                                        </el-icon> 试题篮
                                                    </el-button>
                                                    <el-button link type="primary" size="small"
                                                        @click="viewQuestionDetail(q)">
                                                        {{ shownDetails[q.id] ? '收起解析' : '查看详情' }}
                                                    </el-button>
                                                    <el-button v-if="canManage" link type="danger" size="small"
                                                        @click="handleRemoveQuestion(q)">移除</el-button>
                                                </div>
                                            </div>

                                            <transition name="el-zoom-in-top">
                                                <div v-if="shownDetails[q.id]" class="q-full-detail">
                                                    <div class="q-options" v-if="q.options?.length">
                                                        <div v-for="opt in q.options" :key="opt.id" class="opt-item">
                                                            <span class="opt-label">{{ opt.label }}.</span>
                                                            <div class="opt-text">
                                                                <MdEditor preview-only preview-theme="cyanosis"
                                                                    :model-value="opt.content || ''" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="q-answer-section">
                                                        <div class="ans-block">
                                                            <div class="ans-label">正确答案</div>
                                                            <div class="ans-content">
                                                                <MdEditor preview-only preview-theme="cyanosis"
                                                                    :model-value="q.answer || '未设置'" />
                                                            </div>
                                                        </div>
                                                        <div class="ans-block">
                                                            <div class="ans-label">解析说明</div>
                                                            <div class="ans-content">
                                                                <MdEditor preview-only preview-theme="cyanosis"
                                                                    :model-value="q.explanation || '暂无解析'" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </transition>
                                            <el-divider v-if="index < list.questions.length - 1" />
                                        </div>
                                    </div>
                                </el-tab-pane>
                            </el-tabs>
                        </el-card>
                    </el-col>

                    <!-- Right Column: Sidebar -->
                    <el-col :span="6">
                        <el-card shadow="never" class="sidebar-info-card">
                            <template #header>
                                <div class="sidebar-header">题单信息</div>
                            </template>
                            <div class="info-list">
                                <div class="info-item">
                                    <span class="label">题单编号</span>
                                    <span class="value">#{{ list.id }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="label">创建者</span>
                                    <span class="value creator-name">{{ list.userId === getUser()?.id ? '我' :
                                        (list.user?.username || '用户' +
                                            list.userId) }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="label">题目总数</span>
                                    <span class="value">{{ list.questions?.length || 0 }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="label">题单状态</span>
                                    <el-tag size="small" :type="list.isPublic ? 'success' : 'info'">{{ list.isPublic ?
                                        '公开题单' : '私有题单' }}</el-tag>
                                </div>
                            </div>
                            <div class="sidebar-actions" v-if="canManage">
                                <el-button type="primary" link @click="showEditDialog = true">
                                    <el-icon>
                                        <Edit />
                                    </el-icon> 编辑简介
                                </el-button>
                                <el-button type="danger" link @click="handleDeleteList">
                                    <el-icon>
                                        <Delete />
                                    </el-icon> 删除题单
                                </el-button>
                            </div>
                        </el-card>

                        <!-- Dynamic Ad Cards -->
                        <el-card v-for="ad in adCards" :key="ad.id" shadow="hover" class="sidebar-promo-card">
                            <div class="promo-image-container" v-if="ad.imageUrl">
                                <img :src="ad.imageUrl" :alt="ad.title" class="promo-image" />
                                <div class="promo-tag-top" v-if="ad.badgeText">{{ ad.badgeText }}</div>
                            </div>
                            <div class="promo-content">
                                <div class="promo-title">{{ ad.title }}</div>
                                <div class="promo-tags" v-if="ad.tags">
                                    <el-tag v-for="tag in ad.tags.split(',').filter(t => t.trim())" :key="tag"
                                        size="small" type="success" effect="plain">{{ tag.trim() }}</el-tag>
                                </div>
                                <div class="promo-price-row" v-if="ad.price">
                                    <span class="price-symbol">¥</span>
                                    <span class="price-value">{{ ad.price }}</span>
                                    <span class="price-label">起</span>
                                </div>
                                <el-button type="primary" class="promo-btn"
                                    @click="ad.linkUrl ? window.open(ad.linkUrl, '_blank') : ElMessage.info('课程功能即将上线，敬请期待！')">
                                    立即查看
                                </el-button>
                            </div>
                        </el-card>
                    </el-col>
                </el-row>
            </div>
        </template>


        <!-- Edit Dialog -->
        <el-dialog v-model="showEditDialog" title="编辑题单信息" width="800px">
            <el-form :model="editForm" label-position="top">
                <el-form-item label="题单标题" required>
                    <el-input v-model="editForm.title" placeholder="给题单起个响亮的名字" />
                </el-form-item>
                <el-form-item label="题单简介 (支持 Markdown)">
                    <div class="md-editor-container">
                        <MdEditor v-model="editForm.description" placeholder="在此编写题单的详细介绍、学习目标或复习建议..."
                            :toolbars-exclude="['github', 'save', 'catalog']" style="height: 400px" />
                    </div>
                </el-form-item>
                <el-form-item label="公开展示">
                    <el-switch v-model="editForm.isPublic" />
                    <span class="form-tip">开启后，其他用户可以在广场预览该题单</span>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showEditDialog = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="handleUpdateList">保存修改</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
    ArrowLeft, Calendar, Document, Edit, Delete,
    Download, DocumentCopy, View, User, CircleCheck, Reading,
    Platform, Location, Star, StarFilled
} from '@element-plus/icons-vue'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import api, { getUser } from '@/utils/api'

const route = useRoute()
const router = useRouter()
const listId = route.params.id

const loading = ref(false)
const saving = ref(false)
const list = ref(null)
const showEditDialog = ref(false)
const activeTab = ref('intro')
const shownDetails = reactive({})
const adCards = ref([])

const editForm = reactive({
    title: '',
    description: '',
    isPublic: false
})

const canManage = computed(() => {
    const user = getUser()
    if (!user || !list.value) return false
    return user.id === list.value.userId || user.role === 'admin'
})

const isOwner = computed(() => {
    const user = getUser()
    return user && list.value && user.id === list.value.userId
})

const handleAddToBasket = async (q) => {
    const user = getUser()
    if (!user) return ElMessage.warning('请先登录')

    try {
        await api.post('/api/basket/add', { userId: user.id, questionId: q.id })
        ElMessage.success('已加入试题篮')
    } catch (err) {
        ElMessage.error('加入失败')
    }
}

const fetchDetail = async () => {
    loading.value = true
    try {
        const res = await api.get(`/api/question-lists/${listId}`)
        list.value = res.data
        editForm.title = list.value.title
        editForm.description = list.value.description
        editForm.isPublic = list.value.isPublic
    } catch (err) {
        ElMessage.error('获取题单详情失败')
        router.push('/question-lists')
    } finally {
        loading.value = false
    }
}

const toggleFavorite = async () => {
    if (!list.value) return
    const user = getUser()
    if (!user) return ElMessage.warning('请先登录')

    try {
        const res = await api.post(`/api/question-lists/${list.value.id}/favorite`)
        list.value.isFavorite = res.data.isFavorite
        ElMessage.success(list.value.isFavorite ? '收藏成功' : '已取消收藏')
    } catch (err) {
        ElMessage.error('操作失败')
    }
}

const handleUpdateList = async () => {
    saving.value = true
    try {
        await api.put(`/api/question-lists/${listId}`, editForm)
        ElMessage.success('更新成功')
        showEditDialog.value = false
        fetchDetail()
    } catch (err) {
        ElMessage.error('更新失败')
    } finally {
        saving.value = false
    }
}

const handleDeleteList = () => {
    ElMessageBox.confirm('确定要删除这个题单吗？此操作不可撤销。', '提示', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(async () => {
        await api.delete(`/api/question-lists/${listId}`)
        ElMessage.success('题单已删除')
        router.push('/question-lists')
    }).catch(() => { })
}

const handleRemoveQuestion = (q) => {
    ElMessageBox.confirm(`确定要从题单中移除此题目吗？`, '提示', {
        type: 'info'
    }).then(async () => {
        await api.delete(`/api/question-lists/${listId}/questions/${q.id}`)
        ElMessage.success('已移除')
        fetchDetail()
    }).catch(() => { })
}

const addAllToBasket = async () => {
    if (!list.value?.questions?.length) return

    const user = getUser()
    if (!user) return ElMessage.warning('请先登录')

    try {
        let successCount = 0
        for (const q of list.value.questions) {
            await api.post('/api/basket/add', { userId: user.id, questionId: q.id })
            successCount++
        }
        ElMessage.success(`成功将 ${successCount} 道题目加入试题篮`)
    } catch (err) {
        ElMessage.error('部分题目加入失败')
    }
}

const handleExport = async () => {
    if (!list.value?.id) return

    const user = getUser()
    if (!user) {
        ElMessage.warning('请先登录后再导出')
        router.push({ name: 'Login', query: { redirect: route.fullPath } })
        return
    }

    const token = localStorage.getItem('token')
    const url = `/api/question-lists/${list.value.id}/download${token ? `?token=${token}` : ''}`
    window.open(url, '_blank')
    ElMessage.success('正在为您生成试卷，请稍候...')
}

const viewQuestionDetail = (q) => {
    shownDetails[q.id] = !shownDetails[q.id]
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return date.toLocaleString()
}

const getTypeName = (type) => ({ 1: '单选题', 2: '填空题', 3: '解答题', 4: '证明题', 5: '应用题', 6: '多选题' }[type] || '其他')
const getDifficultName = (diff) => ({ 1: '容易', 2: '较易', 3: '适中', 4: '较难', 5: '困难' }[diff] || '未知')

const fetchAdCards = async () => {
    try {
        const res = await api.get('/api/ad-cards/published')
        adCards.value = res.data.adCards || []
    } catch (err) {
        // silently fail — ads are non-critical
    }
}

onMounted(() => {
    fetchDetail()
    fetchAdCards()
})
</script>

<style scoped>
.question-list-detail {
    min-height: 100vh;
    background-color: #f8fafc;
}

/* Header Styles */
.page-header-container {
    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
    padding: 40px 40px 80px;
    color: white;
}

.page-header {
    max-width: 1200px;
    margin: 0 auto;
}

.header-nav {
    margin-bottom: 32px;
}

.back-btn {
    color: #94a3b8 !important;
    font-weight: 500;
}

.back-btn:hover {
    color: white !important;
}

.title-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.list-title {
    font-size: 32px;
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.02em;
}

.action-btn-premium {
    height: 48px;
    padding: 0 24px;
    font-weight: 700;
    border-radius: 12px;
    background: #ffffff !important;
    color: #2563eb !important;
    border: 2px solid #ffffff !important;
    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.2);
    transition: all 0.3s;
}

.action-btn-premium:hover {
    transform: translateY(-2px);
    background: #f8fafc !important;
    box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.3);
    color: #1e40af !important;
}

.action-btn-secondary {
    height: 48px;
    padding: 0 24px;
    font-weight: 600;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    transition: all 0.3s;
}

.action-btn-secondary:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
}

.status-badge {
    border: none;
    border-radius: 6px;
    font-weight: 700;
    font-size: 10px;
}

.favorite-action-btn {
    color: #ffd700 !important;
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s;
}

.favorite-action-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
}

.favorite-action-btn .btn-text {
    font-size: 14px;
    font-weight: 600;
}

.list-description {
    font-size: 16px;
    color: #bfdbfe;
    max-width: 800px;
    line-height: 1.6;
    margin-bottom: 24px;
}

.list-meta {
    display: flex;
    gap: 24px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

/* Content Body */
.content-body {
    max-width: 1300px;
    margin: -40px auto 40px;
    padding: 0 20px;
}

.main-content-card {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    min-height: 500px;
}

.custom-tabs :deep(.el-tabs__header) {
    margin-bottom: 0;
    padding: 0 24px;
}

.custom-tabs :deep(.el-tabs__nav-wrap::after) {
    height: 1px;
    background-color: #f1f5f9;
}

.custom-tabs :deep(.el-tabs__item) {
    height: 56px;
    font-weight: 600;
    font-size: 15px;
}

/* Intro Section */
.intro-section {
    padding: 32px 24px;
}

.section-title {
    font-size: 20px;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 24px;
    padding-left: 12px;
    border-left: 4px solid #3b82f6;
}

/* Questions Tab */
.actions-embedded-bar {
    background: #f8fafc;
    padding: 12px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f1f5f9;
}

.bar-info {
    font-size: 13px;
    color: #64748b;
    font-weight: 600;
}

.bar-actions {
    display: flex;
    gap: 8px;
}

.questions-container {
    padding: 0 24px 32px;
}

.q-item-wrapper {
    padding: 24px 0;
    animation: slideUp 0.5s ease-out;
}

.paper-style-entry {
    padding: 16px;
    border-radius: 12px;
    transition: background-color 0.2s;
}

.paper-style-entry:hover {
    background-color: #f8fafc;
}

.q-content-wrapper {
    position: relative;
    display: flex;
}

.q-main-body {
    flex: 1;
}

.q-badge-group {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.q-hover-actions {
    position: absolute;
    top: 0;
    right: 0;
    opacity: 0;
    transition: opacity 0.2s;
    background: linear-gradient(to left, #f8fafc 80%, transparent);
    padding-left: 32px;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.paper-style-entry:hover .q-hover-actions {
    opacity: 1;
}

.q-content-preview {
    font-size: 16px;
    line-height: 1.7;
    color: #334155;
}

/* Remove scrollbar from MdEditor preview in question items */
:deep(.md-editor) {
    height: auto !important;
    background: transparent !important;
    border: none !important;
}

:deep(.md-editor-content) {
    overflow: visible !important;
}

:deep(.md-editor-preview-wrapper) {
    padding: 0 !important;
    overflow: visible !important;
}

:deep(.md-editor-preview) {
    overflow: visible !important;
}

.q-full-detail {
    margin-top: 24px;
    padding: 24px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #f1f5f9;
}

.q-options {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.opt-item {
    display: flex;
    gap: 8px;
}

.opt-label {
    font-weight: 700;
    color: #3b82f6;
}

.q-answer-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-top: 20px;
    border-top: 1px dashed #e2e8f0;
}

.ans-label {
    font-weight: 800;
    font-size: 13px;
    color: #64748b;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.ans-content {
    color: #1e293b;
}

/* Sidebar Styles */
.sidebar-info-card {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.sidebar-header {
    font-weight: 800;
    font-size: 14px;
    color: #475569;
}

.info-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.info-item .label {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 500;
}

.info-item .value {
    font-size: 14px;
    color: #1e293b;
    font-weight: 600;
}

.creator-name {
    color: #7c3aed !important;
}

.sidebar-actions {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #f1f5f9;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
}

.md-editor-container {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
}

.form-tip {
    font-size: 12px;
    color: #94a3b8;
    margin-left: 12px;
}

/* Sidebar Promotion Card */
.sidebar-promo-card {
    margin-top: 24px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #f1f5f9;
    --el-card-padding: 0px;
}

.promo-image-container {
    position: relative;
    height: 140px;
    overflow: hidden;
}

.promo-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.sidebar-promo-card:hover .promo-image {
    transform: scale(1.05);
}

.promo-tag-top {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
}

.promo-content {
    padding: 16px;
}

.promo-title {
    font-size: 16px;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 12px;
    line-height: 1.4;
}

.promo-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
}

.promo-price-row {
    margin-bottom: 16px;
    display: flex;
    align-items: baseline;
    gap: 2px;
}

.price-symbol {
    font-size: 14px;
    font-weight: 700;
    color: #f43f5e;
}

.price-value {
    font-size: 24px;
    font-weight: 900;
    color: #f43f5e;
}

.price-label {
    font-size: 12px;
    color: #94a3b8;
    margin-left: 4px;
}

.promo-btn {
    width: 100%;
    font-weight: 700;
    border-radius: 8px;
}

/* Animations */
.animation-fadeIn {
    animation: fadeIn 0.6s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.loading-state {
    height: 400px;
}
</style>
